import React, { useEffect, useMemo, useRef, useState } from "react";
import { exportFile, showError } from "../../../utils/utilities";
import { useTranslation } from "react-i18next";
import { DownloadOutlined } from "@ant-design/icons";
import ExportSFApi from "../../../services/api/ExportSF";
import { get, debounce, isEmpty } from "lodash";
import { fileFormatXextension } from "../../../utils/properties";
import { useFormik } from "formik";
import Filter from "../../../components/functional_components/Filter/Filter";
import Project from "../../../services/api/Project";
import ProjectEstimateStatus from "../../../services/api/ProjectEstimateStatus";

const ExportSF = () => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    loading: false,
    societiesList: [],
    fetchingSocieties: false,
    sellingStatusList: [],
  });
  const fetchSocietiesRef = useRef(0);

  useEffect(() => {
    getSellingStatus();
  }, []);

  const downloadSF = async (filters) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const exportResponse = await ExportSFApi.exportSF(filters);
    if (!exportResponse.error) {
      const downloadType = fileFormatXextension["csv"];
      const blob = new Blob([exportResponse], { type: downloadType });
      exportFile(blob, "export-sf.csv");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const getSocieties = async (searchString) => {
    const response = await Project.getSocieties(`allPages=true&societyName=${searchString}`);
    const dataArr = [];
    if (response && !response.error && response.data) {
      response.data.forEach((item) => {
        dataArr.push({ label: item, value: item });
      });
    }
    return dataArr;
  };

  const onSearchSocieties = useMemo(() => {
    const loadOptions = (searchString) => {
      fetchSocietiesRef.current += 1;
      const fetchId = fetchSocietiesRef.current;
      setState((prevState) => ({ ...prevState, societiesList: [], fetchingSocieties: true }));
      getSocieties(searchString).then((newOptions) => {
        if (fetchId !== fetchSocietiesRef.current) {
          // for fetch callback order
          return;
        }
        setState((prevState) => ({ ...prevState, societiesList: newOptions, fetchingSocieties: false }));
      });
    };
    return debounce(loadOptions, 300);
  }, [getSocieties]);

  const getSellingStatus = async () => {
    const response = await ProjectEstimateStatus.getStatusesList("allPages=true");
    const optionList = [];
    if (response && !response.error && response.data) {
      response.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        sellingStatusList: optionList,
      }));
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
  };

  const contractType = [
    {
      label: t("projects.detail.planning"),
      value: "PLANNING",
    },
    {
      label: t("projects.detail.royalties"),
      value: "ROYALTIES",
    },
    {
      label: t("projects.detail.windTunnel"),
      value: "WIND_TUNNEL",
    },
    {
      label: t("projects.detail.otherIncome"),
      value: "OTHER_INCOME",
    },
  ];

  const exportOptions = [
    { label: t("exportSF.filter.detailedExport"), value: "true" },
    { label: t("exportSF.filter.summaryExport"), value: "false" },
  ];

  const formikFilter = useFormik({
    initialValues: {
      society: [],
      projectStatus: [],
      sellingStatus: [],
      contractType: [],
      detailed: "true",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      Object.keys(values).forEach((key) => {
        if (isEmpty(values[key])) {
          delete values[key];
        }
      });
      downloadSF(values);
    },
  });

  const fieldsFilter = [
    {
      type: "selectWithSearch",
      mode: "multiple",
      allowClear: true,
      isRequired: false,
      id: "society",
      name: "society",
      value: formikFilter.values.society,
      label: t("exportSF.filter.society"),
      placeholder: t("exportSF.filter.societyPlaceholder"),
      onChange: (value) => formikFilter.setFieldValue("society", value),
      onSearch: (value) => onSearchSocieties(value),
      onBlur: formikFilter.handleBlur("society"),
      dataSelect: state.societiesList,
      loading: state.fetchingSocieties,
    },
    {
      type: "select",
      mode: "multiple",
      allowClear: true,
      isRequired: false,
      id: "projectStatus",
      name: "projectStatus",
      value: formikFilter.values.projectStatus,
      label: t("exportSF.filter.projectStatus"),
      onChange: (value) => formikFilter.setFieldValue("projectStatus", value),
      onBlur: formikFilter.handleBlur("projectStatus"),
      dataSelect: [
        { label: t("projectsManagement.filter.select.blocked"), value: "BLOCKED" },
        { label: t("projectsManagement.filter.select.closed"), value: "CLOSED" },
        { label: t("projectsManagement.filter.select.completed"), value: "COMPLETED" },
        { label: t("projectsManagement.filter.select.created"), value: "CREATED" },
        { label: t("projectsManagement.filter.select.planning"), value: "PLANNING" },
        { label: t("projectsManagement.filter.select.released"), value: "RELEASED" },
        { label: t("projectsManagement.filter.select.unknown"), value: "UNKNOWN" },
      ],
    },
    {
      type: "select",
      mode: "multiple",
      allowClear: true,
      isRequired: false,
      id: "sellingStatus",
      name: "sellingStatus",
      value: formikFilter.values.sellingStatus,
      label: t("exportSF.filter.sellingStatus"),
      onChange: (value) => formikFilter.setFieldValue("sellingStatus", value),
      onBlur: formikFilter.handleBlur("sellingStatus"),
      dataSelect: state.sellingStatusList,
    },
    {
      type: "select",
      mode: "multiple",
      allowClear: true,
      isRequired: false,
      id: "contractType",
      name: "contractType",
      value: formikFilter.values.contractType,
      label: t("exportSF.filter.contractType"),
      onChange: (value) => formikFilter.setFieldValue("contractType", value),
      onBlur: formikFilter.handleBlur("contractType"),
      dataSelect: contractType,
    },
    {
      type: "select",
      allowClear: false,
      isRequired: true,
      id: "detailed",
      name: "detailed",
      value: formikFilter.values.detailed,
      label: t("exportSF.filter.exportOptions"),
      onChange: (value) => formikFilter.setFieldValue("detailed", value),
      onBlur: formikFilter.handleBlur("detailed"),
      dataSelect: exportOptions,
    },
  ];

  return (
    <Filter
      formikForm={formikFilter}
      fields={fieldsFilter}
      loading={state.loading}
      allInOneRow={false}
      onReset={() => {}}
      submitIcon={<DownloadOutlined className="icon-large" />}
    />
  );
};

export default ExportSF;
